import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, CheckCircle, XCircle, PencilSimple, Trash, Star } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import DocumentUploader from './DocumentUploader';

const ESCOLARIDADES = [
  'Ensino Fundamental',
  'Ensino Médio',
  'Técnico em Eletrotécnica',
  'Técnico em Eletrônica',
  'Superior em Engenharia Elétrica',
  'Superior em Engenharia Eletrônica',
  'Pós-graduação'
];

const HORARIOS = [
  'Manhã (6h-12h)',
  'Tarde (12h-18h)',
  'Noite (18h-22h)',
  'Integral',
  'Plantão 24h'
];

const ESPECIALIDADES = [
  'Instalações Residenciais',
  'Instalações Comerciais',
  'Instalações Industriais',
  'Manutenção Preventiva',
  'Manutenção Corretiva',
  'Automação',
  'Painéis Elétricos',
  'Alta Tensão',
  'Baixa Tensão',
  'SPDA (Para-raios)',
  'Ar Condicionado',
  'Energia Solar'
];

const StarRating = ({ value, onChange, readOnly = false, size = 16 }) => (
  <div className="flex gap-1">
    {[1,2,3,4,5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => !readOnly && onChange && onChange(star)}
        className={`${readOnly ? '' : 'cursor-pointer hover:scale-110'} transition-transform`}
        disabled={readOnly}
      >
        <Star size={size} weight={star <= value ? 'fill' : 'regular'} className={star <= value ? 'text-[#FFB800]' : 'text-zinc-600'} />
      </button>
    ))}
  </div>
);

const AuxiliarManagement = () => {
  const [auxiliares, setAuxiliares] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEspecialidades, setSelectedEspecialidades] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedAuxiliar, setSelectedAuxiliar] = useState(null);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({
    name: '', cpf: '', rg: '',
    cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
    telefone_fixo: '', telefone_celular: '',
    escolaridade: '', horario_disponivel: '',
    valor_hora: '', taxa_deslocamento: '',
    nivel_tecnico: 3
  });

  useEffect(() => { fetchAuxiliares(); }, []);

  const fetchAuxiliares = async () => {
    try {
      const response = await api.get('/auxiliares');
      setAuxiliares(response.data);
    } catch (error) {
      console.error('Error fetching auxiliares:', error);
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({ ...prev, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }));
        }
      } catch (error) { console.error('Erro ao buscar CEP:', error); }
    }
  };

  const formatCPF = (v) => v.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  const formatCEP = (v) => v.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
  const formatPhone = (v) => { const p = v.replace(/\D/g, ''); return p.length <= 10 ? p.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3') : p.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); };

  const toggleEspecialidade = (esp) => {
    setSelectedEspecialidades(prev => prev.includes(esp) ? prev.filter(e => e !== esp) : [...prev, esp]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedEspecialidades.length === 0) { alert('Selecione pelo menos uma especialidade'); return; }
    try {
      await api.post('/auxiliares', {
        ...formData,
        especialidades: selectedEspecialidades,
        valor_hora: parseFloat(formData.valor_hora),
        taxa_deslocamento: parseFloat(formData.taxa_deslocamento),
        nivel_tecnico: formData.nivel_tecnico
      });
      setIsDialogOpen(false);
      setFormData({ name: '', cpf: '', rg: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', telefone_fixo: '', telefone_celular: '', escolaridade: '', horario_disponivel: '', valor_hora: '', taxa_deslocamento: '', nivel_tecnico: 3 });
      setSelectedEspecialidades([]);
      fetchAuxiliares();
    } catch (error) {
      alert('Erro ao criar auxiliar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEdit = (aux) => {
    setSelectedAuxiliar(aux);
    setEditData({
      name: aux.name || '', cpf: aux.cpf || '', rg: aux.rg || '',
      cep: aux.cep || '', endereco: aux.endereco || '', numero: aux.numero || '', complemento: aux.complemento || '',
      bairro: aux.bairro || '', cidade: aux.cidade || '', estado: aux.estado || '',
      telefone_fixo: aux.telefone_fixo || '', telefone_celular: aux.telefone_celular || '',
      escolaridade: aux.escolaridade || '', horario_disponivel: aux.horario_disponivel || '',
      especialidades: aux.especialidades || [],
      valor_hora: String(aux.valor_hora || ''), taxa_deslocamento: String(aux.taxa_deslocamento || ''),
      nivel_tecnico: aux.nivel_tecnico || 1
    });
    setIsEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedAuxiliar) return;
    try {
      await api.put(`/auxiliares/${selectedAuxiliar.id}`, {
        name: editData.name, cpf: editData.cpf, rg: editData.rg,
        cep: editData.cep, endereco: editData.endereco, numero: editData.numero,
        complemento: editData.complemento || null, bairro: editData.bairro, cidade: editData.cidade, estado: editData.estado,
        telefone_fixo: editData.telefone_fixo || null, telefone_celular: editData.telefone_celular,
        escolaridade: editData.escolaridade, horario_disponivel: editData.horario_disponivel,
        especialidades: editData.especialidades,
        valor_hora: parseFloat(editData.valor_hora), taxa_deslocamento: parseFloat(editData.taxa_deslocamento),
        nivel_tecnico: editData.nivel_tecnico
      });
      setIsEditOpen(false);
      setSelectedAuxiliar(null);
      fetchAuxiliares();
    } catch (error) { alert('Erro ao editar auxiliar: ' + (error.response?.data?.detail || error.message)); }
  };

  const handleDelete = async () => {
    if (!selectedAuxiliar) return;
    try {
      await api.delete(`/auxiliares/${selectedAuxiliar.id}`);
      setIsDeleteOpen(false);
      setSelectedAuxiliar(null);
      fetchAuxiliares();
    } catch (error) { alert('Erro ao excluir auxiliar: ' + (error.response?.data?.detail || error.message)); }
  };

  const updateStatus = async (id, newStatus) => {
    try { await api.patch(`/auxiliares/${id}/status?status=${newStatus}`); fetchAuxiliares(); }
    catch (error) { console.error('Error updating status:', error); }
  };

  const renderForm = (data, setData, onSubmit, submitLabel, isCreate = false) => (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="border-b border-[#27272A] pb-4">
        <h3 className="text-[#FFB800] font-semibold mb-3">Dados Pessoais</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Nome Completo *</Label>
            <Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="auxiliar-name-input" />
          </div>
          <div>
            <Label>CPF *</Label>
            <Input value={data.cpf} onChange={(e) => setData({...data, cpf: isCreate ? formatCPF(e.target.value) : e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="000.000.000-00" maxLength={14} required />
          </div>
          <div>
            <Label>RG *</Label>
            <Input value={data.rg} onChange={(e) => setData({...data, rg: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
          </div>
        </div>
      </div>

      <div className="border-b border-[#27272A] pb-4">
        <h3 className="text-[#FFB800] font-semibold mb-3">Nivel Tecnico</h3>
        <div className="flex items-center gap-4">
          <StarRating value={data.nivel_tecnico} onChange={(v) => setData({...data, nivel_tecnico: v})} size={24} />
          <span className="text-zinc-400 text-sm">{data.nivel_tecnico} estrela(s)</span>
        </div>
      </div>

      <div className="border-b border-[#27272A] pb-4">
        <h3 className="text-[#FFB800] font-semibold mb-3">Endereço</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>CEP *</Label>
            <Input value={data.cep} onChange={(e) => { const cep = isCreate ? formatCEP(e.target.value) : e.target.value; setData({...data, cep}); if (isCreate && cep.replace(/\D/g, '').length === 8) buscarCEP(cep); }} className="bg-[#050505] border-[#27272A] text-white" placeholder="00000-000" maxLength={9} required />
          </div>
          <div className="col-span-2">
            <Label>Logradouro *</Label>
            <Input value={data.endereco} onChange={(e) => setData({...data, endereco: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
          </div>
          <div><Label>Número *</Label><Input value={data.numero} onChange={(e) => setData({...data, numero: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required /></div>
          <div><Label>Complemento</Label><Input value={data.complemento} onChange={(e) => setData({...data, complemento: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" /></div>
          <div><Label>Bairro *</Label><Input value={data.bairro} onChange={(e) => setData({...data, bairro: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required /></div>
          <div><Label>Cidade *</Label><Input value={data.cidade} onChange={(e) => setData({...data, cidade: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required /></div>
          <div><Label>Estado *</Label><Input value={data.estado} onChange={(e) => setData({...data, estado: e.target.value.toUpperCase()})} className="bg-[#050505] border-[#27272A] text-white" maxLength={2} required /></div>
        </div>
      </div>

      <div className="border-b border-[#27272A] pb-4">
        <h3 className="text-[#FFB800] font-semibold mb-3">Telefones</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Telefone Fixo</Label><Input value={data.telefone_fixo} onChange={(e) => setData({...data, telefone_fixo: isCreate ? formatPhone(e.target.value) : e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="(00) 0000-0000" /></div>
          <div><Label>Telefone Celular *</Label><Input value={data.telefone_celular} onChange={(e) => setData({...data, telefone_celular: isCreate ? formatPhone(e.target.value) : e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="(00) 00000-0000" required /></div>
        </div>
      </div>

      <div className="border-b border-[#27272A] pb-4">
        <h3 className="text-[#FFB800] font-semibold mb-3">Dados Profissionais</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Escolaridade *</Label>
            <Select value={data.escolaridade || '_none'} onValueChange={(v) => setData({...data, escolaridade: v === '_none' ? '' : v})}>
              <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white">{ESCOLARIDADES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Horário Disponível *</Label>
            <Select value={data.horario_disponivel || '_none'} onValueChange={(v) => setData({...data, horario_disponivel: v === '_none' ? '' : v})}>
              <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white">{HORARIOS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Especialidades *</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ESPECIALIDADES.map(esp => (
                <Badge key={esp}
                  onClick={() => {
                    const current = isCreate ? (selectedEspecialidades) : (data.especialidades || []);
                    const updated = current.includes(esp) ? current.filter(e => e !== esp) : [...current, esp];
                    if (isCreate) setSelectedEspecialidades(updated); else setData({...data, especialidades: updated});
                  }}
                  className={`cursor-pointer ${(isCreate ? selectedEspecialidades : (data.especialidades || [])).includes(esp) ? 'bg-[#FFB800] text-black hover:bg-[#E6A600]' : 'bg-[#1E1E1E] text-zinc-400 hover:bg-[#27272A]'}`}
                >{esp}</Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-[#FFB800] font-semibold mb-3">Dados Financeiros</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Valor por Hora (R$) *</Label><Input type="number" step="0.01" value={data.valor_hora} onChange={(e) => setData({...data, valor_hora: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="0.00" required /></div>
          <div><Label>Taxa de Deslocamento (R$) *</Label><Input type="number" step="0.01" value={data.taxa_deslocamento} onChange={(e) => setData({...data, taxa_deslocamento: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="0.00" required /></div>
        </div>
      </div>

      <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600] py-3" data-testid="submit-auxiliar-button">{submitLabel}</Button>
    </form>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>Gerenciar Auxiliares</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="add-auxiliar-button"><Plus size={20} className="mr-2" />Novo Auxiliar</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Cadastrar Novo Auxiliar</DialogTitle></DialogHeader>
            <p className="text-zinc-500 text-xs mb-2">Auxiliares não possuem acesso ao sistema (sem login/senha).</p>
            {renderForm(formData, setFormData, handleSubmit, 'Cadastrar Auxiliar', true)}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#121212] border border-[#27272A] rounded-sm overflow-x-auto">
        <table className="w-full text-sm text-left text-zinc-400">
          <thead className="text-xs uppercase bg-[#050505] text-zinc-400">
            <tr>
              <th className="px-6 py-3">Nome</th>
              <th className="px-6 py-3">CPF</th>
              <th className="px-6 py-3">Nivel</th>
              <th className="px-6 py-3">Especialidades</th>
              <th className="px-6 py-3">Valor/Hora</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {auxiliares.map(aux => (
              <tr key={aux.id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]" data-testid={`auxiliar-row-${aux.cpf}`}>
                <td className="px-6 py-4 text-white font-medium">{aux.name}</td>
                <td className="px-6 py-4">{aux.cpf}</td>
                <td className="px-6 py-4"><StarRating value={aux.nivel_tecnico || 1} readOnly size={14} /></td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {aux.especialidades?.slice(0, 2).map((esp, idx) => (<span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded">{esp}</span>))}
                    {aux.especialidades?.length > 2 && <span className="text-xs text-[#FFB800]">+{aux.especialidades.length - 2}</span>}
                  </div>
                </td>
                <td className="px-6 py-4 text-[#22C55E] font-semibold">R$ {aux.valor_hora?.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <Badge className={`${aux.status === 'ativo' ? 'bg-[#22C55E]' : aux.status === 'pendente' ? 'bg-[#FFB800] text-black' : 'bg-[#EF4444]'}`}>{aux.status}</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2 items-center">
                    {aux.status !== 'ativo' && <button onClick={() => updateStatus(aux.id, 'ativo')} className="text-[#22C55E] hover:text-[#16A34A]" title="Ativar"><CheckCircle size={18} /></button>}
                    {aux.status !== 'inativo' && <button onClick={() => updateStatus(aux.id, 'inativo')} className="text-[#EF4444] hover:text-[#DC2626]" title="Inativar"><XCircle size={18} /></button>}
                    <button onClick={() => openEdit(aux)} className="text-zinc-400 hover:text-[#FFB800]" data-testid={`edit-auxiliar-${aux.cpf}`}><PencilSimple size={16} /></button>
                    <button onClick={() => { setSelectedAuxiliar(aux); setIsDeleteOpen(true); }} className="text-zinc-400 hover:text-red-500" data-testid={`delete-auxiliar-${aux.cpf}`}><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {auxiliares.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">Nenhum auxiliar cadastrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Editar Auxiliar</DialogTitle></DialogHeader>
          {renderForm(editData, setEditData, handleEdit, 'Salvar Alterações', false)}
          {selectedAuxiliar && (
            <div className="border-t border-[#27272A] pt-4 mt-4">
              <DocumentUploader
                entityId={selectedAuxiliar.id}
                entityType="auxiliares"
                documents={selectedAuxiliar.documentos_urls || []}
                onUpdate={() => fetchAuxiliares()}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader><DialogTitle className="text-white">Excluir Auxiliar</DialogTitle></DialogHeader>
          {selectedAuxiliar && (
            <div className="space-y-4">
              <p className="text-zinc-400">Tem certeza que deseja excluir o auxiliar <strong className="text-white">{selectedAuxiliar.name}</strong>?</p>
              <p className="text-red-400 text-xs">Esta ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <Button onClick={() => setIsDeleteOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]">Cancelar</Button>
                <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-auxiliar">Excluir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuxiliarManagement;
